import React from "react";
import { Link } from "react-router-dom";
import SEO from "../utils/SEO";
import SubscriptionComponent from "../utils/SubscriptionComponent";
import AtlasGraphBackdrop from "../home/AtlasGraphBackdrop";
import ABOUT_SERVICES from "../../data/aboutServices";
import stats from "../../data/atlasStats.json";
import { REVIEWER, EDITORIAL_PATH } from "../../config/eeat";
import { trackCTAClick } from "../../utils/analytics";
import "../../styles/sarv-system.css";
import "../../styles/About.css";

// /mission — rebuilt on the homepage's design system (sarv-system.css): Inter,
// the dark botanical palette, the same section rhythm and card primitives.
//
// The copy is the existing Mission / MissionStatement / about-Services text,
// re-laid out rather than rewritten. The feature definitions moved to
// data/aboutServices.js. (The /services route is a different page —
// components/services/Services.js, about practitioners — and is untouched.)
//
// This route IS prerendered (it's in scripts/prerender-pages.js ROUTES), so the
// same hydration discipline as the homepage applies: no Math.random, no window
// measurement during render, and figures formatted deterministically.

const n = (v) => String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const Mission = () => (
  <main className="sv-dark about">
    <SEO
      title="About Sarv - Personalized Holistic Health Platform"
      description="Learn about Sarv's mission to bring personalized herbal medicine and holistic health recommendations to everyone. Science-backed natural remedies based on your unique health profile."
      canonicalPath="/mission"
      keywords="about Sarv, holistic health mission, personalized herbal medicine company, natural health platform, M31-V1 LLC, Ayurveda wellness app, herbal medicine technology"
    />

    {/* ---------------------------------------------------------------- hero */}
    <section className="about__hero">
      <AtlasGraphBackdrop intensity={0.55} density={72} />
      <div className="sv-wrap about__hero-inner">
        <p className="sv-eyebrow">About Sârv</p>
        <h1 className="sv-display about__title">Our mission</h1>
        <p className="sv-lede about__lede">
          To put the wisdom of traditional herbal medicine and the discipline of
          modern evidence in the same place — and to be honest about which is
          which.
        </p>
      </div>
    </section>

    {/* ------------------------------------------------------------ overview */}
    <section className="sv-section about__band about__band--2">
      <div className="sv-wrap about__two">
        <div>
          <h2 className="sv-h2 about__h2">Why we exist</h2>
        </div>
        <div className="about__prose">
          <p>
            Sârv is dedicated to revolutionizing the way individuals approach
            healthcare by intertwining the wisdom of traditional herbal remedies
            with the precision of modern technology. Our mission is to empower
            users with personalized, natural healthcare solutions that align
            with their unique physical characteristics and health conditions.
          </p>
          <p>
            At the heart of that is a belief in individualized natural
            healthcare, tailored to the needs and goals of each person. The
            platform is not just about suggesting herbal solutions; it is about
            educating people on the medicinal properties of plants so they can
            make informed choices about their own health.
          </p>
          <p>
            Sârv is not just an app; it's a movement towards a more natural,
            informed and personalized healthcare paradigm, where the power of
            nature is harnessed in harmony with technological innovation to
            promote health, healing and harmony.
          </p>
        </div>
      </div>
    </section>

    {/* --------------------------------------------------------- the numbers */}
    <section className="sv-section about__band">
      <div className="sv-wrap">
        <h2 className="sv-h2 sv-center about__h2">What that looks like</h2>
        <p className="sv-body sv-center about__intro">
          Not a slogan — a corpus. Every figure below is generated from our own
          knowledge graph at build time.
        </p>
        <div className="sv-bento about__bento">
          <article className="sv-card sv-span-3 about__card">
            <p className="about__figure">{n(stats.edges)}</p>
            <h3 className="sv-h4">graded relationships</h3>
            <p className="sv-body about__body">
              Between {n(stats.herbs)} plants, {n(stats.conditions)} conditions
              and {n(stats.actions)} pharmacological actions — each carrying the
              strength of the evidence behind it, from traditional use to
              clinical trial.
            </p>
            <Link
              className="about__link"
              to="/atlas"
              onClick={() => trackCTAClick("about_atlas", "mission")}
            >
              Explore the Atlas →
            </Link>
          </article>

          <article className="sv-card sv-span-3 about__card">
            <p className="about__figure">{stats.folkShare}%</p>
            <h3 className="sv-h4">of it is folklore — and we say so</h3>
            <p className="sv-body about__body">
              Most of what is written about herbs is traditional use with no
              science reviewed. That isn't worthless, but it isn't evidence.
              Labelling the difference honestly is the whole point of Sârv.
            </p>
          </article>

          <article className="sv-card sv-span-2 about__card">
            <p className="about__figure about__figure--sm">
              {stats.unsafeHerbs}
            </p>
            <h3 className="sv-h5">plants hidden by default</h3>
            <p className="sv-body about__body">
              Rated unsafe for self-administration. You can ask to see them; we
              won't volunteer them.
            </p>
          </article>

          <article className="sv-card sv-span-2 about__card">
            <p className="about__figure about__figure--sm">
              {n(stats.sourceRefs)}
            </p>
            <h3 className="sv-h5">named references</h3>
            <p className="sv-body about__body">
              Commission E, WHO monographs, published trials — citations you can
              look up, not "studies show".
            </p>
          </article>

          <article className="sv-card sv-span-2 about__card">
            <h3 className="sv-h5">Reviewed by people</h3>
            <p className="sv-body about__body">
              Editorial content is reviewed by {REVIEWER.name}, and we publish
              how we work.
            </p>
            <Link className="about__link" to={EDITORIAL_PATH}>
              Our editorial process →
            </Link>
          </article>
        </div>
      </div>
    </section>

    {/* ------------------------------------------------------------- offering */}
    <section className="sv-section about__band about__band--2">
      <div className="sv-wrap">
        <h2 className="sv-h2 sv-center about__h2">What we offer</h2>
        <p className="sv-body sv-center about__intro">Backed by science.</p>

        <div className="about__offer">
          {ABOUT_SERVICES.map((service) => (
            <article key={service.title} className="sv-card about__offer-card">
              <div className="sv-card__media about__offer-media">
                <img src={service.imgSrc} alt="" loading="lazy" />
              </div>
              <h3 className="sv-h4 about__offer-title">{service.title}</h3>
              <p className="sv-body about__body">{service.summary}</p>
            </article>
          ))}
        </div>

        <div className="sv-center about__actions">
          <Link
            to="/start?source=mission"
            className="sv-btn sv-btn--primary"
            onClick={() => trackCTAClick("about_start_plan", "mission")}
          >
            Get your personalized plan
          </Link>
          <Link to="/atlas" className="sv-btn sv-btn--ghost">
            Explore the Atlas
          </Link>
        </div>
      </div>
    </section>

    <p className="about__disclaimer">
      Sârv is for general wellness and educational purposes only and is not a
      substitute for professional medical advice, diagnosis or treatment.
    </p>

    {/* Shared with the light pages, so it is skinned dark here the same way the
        homepage does it — see .home-subscribe / .about-subscribe in the CSS. */}
    <div className="about-subscribe">
      <SubscriptionComponent />
    </div>
  </main>
);

export default Mission;
